import React from 'react';
import { Users, GraduationCap, Heart, School } from 'lucide-react';
import { Avatar } from '../../shared/Avatar';
import { MasteryBar } from '../../shared/MasteryBar';
import { Badge } from '../../../../components/ui/Badge';
import { formatRelativeTime } from '../../../../utils/formatters';
import { ROLE_CONFIG } from './types';
import type { UserRow, RoleFilter } from './types';
export const learnerColumns = [{
  key: 'name',
  label: 'Naam',
  render: (v: string, row: UserRow) => <div className="flex items-center gap-2.5" data-id="element-4413">
        <Avatar name={v} data-id="element-4414" />
        <div data-id="element-4415">
          <div className="text-sm font-medium text-gray-900" data-id="element-4416">{v}</div>
          <div className="text-xs text-gray-400" data-id="element-4417">
            {row.grade ? `Groep ${row.grade}` : '—'}
          </div>
        </div>
      </div>
}, {
  key: 'organization',
  label: 'School',
  render: (v: string) => <span className="text-sm text-gray-600" data-id="element-4418">{v}</span>
}, {
  key: 'sessionCount',
  label: 'Sessies',
  render: (v: number) => <span className="text-sm font-medium text-gray-700 tabular-nums" data-id="element-4419">
        {v}
      </span>
}, {
  key: 'avgMastery',
  label: 'Beheersing',
  render: (v: number) => <MasteryBar value={v || 0} data-id="element-4420" />
}, {
  key: 'lastActive',
  label: 'Laatste activiteit',
  render: (v: number) => <span className="text-xs text-gray-500" data-id="element-4421">
        {formatRelativeTime(v, true)}
      </span>
}];
export const teacherColumns = [{
  key: 'name',
  label: 'Naam',
  render: (v: string, row: UserRow) => <div className="flex items-center gap-2.5" data-id="element-4422">
        <Avatar name={v} data-id="element-4423" />
        <div data-id="element-4424">
          <div className="text-sm font-medium text-gray-900" data-id="element-4425">{v}</div>
          <div className="text-xs text-gray-400" data-id="element-4426">
            {row.groupName ?? (row.account && 'grade' in row.account ? `Groep ${row.account.grade}` : '—')}
          </div>
        </div>
      </div>
}, {
  key: 'organization',
  label: 'School',
  render: (v: string) => <span className="text-sm text-gray-600" data-id="element-4427">{v}</span>
}, {
  key: 'linkedCount',
  label: 'Leerlingen',
  render: (v: number) => <span className="inline-flex items-center gap-1 text-sm font-medium text-gray-700" data-id="element-4428">
        <Users className="w-3.5 h-3.5 text-gray-400" data-id="element-4429" />
        {v}
      </span>
}, {
  key: 'lastActive',
  label: 'Laatste activiteit',
  render: (v: number) => <span className="text-xs text-gray-500" data-id="element-4430">
        {formatRelativeTime(v, true)}
      </span>
}];
export const parentColumns = [{
  key: 'name',
  label: 'Naam',
  render: (v: string) => <div className="flex items-center gap-2.5" data-id="element-4431">
        <Avatar name={v} data-id="element-4432" />
        <span className="text-sm font-medium text-gray-900" data-id="element-4433">{v}</span>
      </div>
}, {
  key: 'linkedCount',
  label: 'Kinderen',
  render: (v: number) => <span className="inline-flex items-center gap-1 text-sm font-medium text-gray-700" data-id="element-4434">
        <Heart className="w-3.5 h-3.5 text-rose-400" data-id="element-4435" />
        {v}
      </span>
}, {
  key: 'lastActive',
  label: 'Laatste activiteit',
  render: (v: number) => <span className="text-xs text-gray-500" data-id="element-4436">
        {formatRelativeTime(v, true)}
      </span>
}];
export const allColumns = [{
  key: 'name',
  label: 'Gebruiker',
  render: (_value: unknown, row: UserRow) => <div className="flex items-center gap-3" data-id="element-4437">
        <Avatar name={row.name} data-id="element-4438" />
        <div data-id="element-4439">
          <div className="font-medium text-gray-900 text-sm" data-id="element-4440">{row.name}</div>
          <div className="text-xs text-gray-400" data-id="element-4441">{row.email}</div>
        </div>
      </div>
}, {
  key: 'role',
  label: 'Rol',
  render: (_value: unknown, row: UserRow) => <Badge variant={ROLE_CONFIG[row.role].variant} size="sm" data-id="element-4442">
        {ROLE_CONFIG[row.role].label}
      </Badge>
}, {
  key: 'organization',
  label: 'Organisatie',
  render: (v: string) => <span className="text-sm text-gray-600 truncate max-w-[140px] block" data-id="element-4443">
        {v}
      </span>
}, {
  key: 'linkedCount',
  label: 'Koppelingen',
  render: (v: number) => <span className="text-sm text-gray-700" data-id="element-4444">{v > 0 ? v : '—'}</span>
}, {
  key: 'xp',
  label: 'XP',
  render: (v: number, row: UserRow) => row.role === 'learner' ? <span className="text-sm font-bold text-violet-600" data-id="element-4445">
          {v.toLocaleString('nl-NL')}
        </span> : <span className="text-xs text-gray-400" data-id="element-4446">—</span>
}, {
  key: 'lastActiveFormatted',
  label: 'Actief',
  render: (v: string) => <span className="text-xs text-gray-500" data-id="element-4447">{v}</span>
}];
export const schoolColumns = [{
  key: 'name',
  label: 'School',
  render: (v: string) => <div className="flex items-center gap-2.5" data-id="element-4448">
        <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0" data-id="element-4449">
          <School className="w-4 h-4 text-purple-600" data-id="element-4450" />
        </div>
        <span className="text-sm font-medium text-gray-900" data-id="element-4451">{v}</span>
      </div>
}, {
  key: 'learnerCount',
  label: 'Leerlingen',
  render: (v: number) => <span className="inline-flex items-center gap-1 text-sm font-medium text-gray-700" data-id="element-4452">
        <Users className="w-3.5 h-3.5 text-gray-400" data-id="element-4453" />
        {v}
      </span>
}, {
  key: 'teacherCount',
  label: 'Docenten',
  render: (v: number) => <span className="inline-flex items-center gap-1 text-sm font-medium text-gray-700" data-id="element-4454">
        <GraduationCap className="w-3.5 h-3.5 text-gray-400" data-id="element-4455" />
        {v}
      </span>
}, {
  key: 'avgMastery',
  label: 'Gem. beheersing',
  render: (v: number) => <MasteryBar value={v} data-id="element-4456" />
}];
export function getColumnsForRole(roleFilter: RoleFilter) {
  switch (roleFilter) {
    case 'learner':
      return learnerColumns;
    case 'teacher':
      return teacherColumns;
    case 'parent':
      return parentColumns;
    case 'school':
      return schoolColumns;
    default:
      return allColumns;
  }
}